import React, { useEffect, useRef } from 'react';

import { keyframes } from '@compiled/react';
import { useIntl } from 'react-intl-next';

import { cssMap, cx } from '@atlaskit/css';
import { Box, Inline } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { getFeatureInfo } from '../../../common/getFeatureInfo';
import type { AudioFeature } from '../../../types/audioFeature';
import { useAudioPlayerStore } from '../../hooks/useAudioPlayerStore';
import { PlaylistItemIcon } from '../playlist/PlaylistItemIcon';

export type AudioIconStackProps = {
	featureList: AudioFeature[];
};

const RightToLeftKeyframes = keyframes({
	'0%': {
		transform: 'translateX(-54px) rotate(-12deg)',
	},
	'100%': {
		transform: 'translateX(-3px) rotate(12deg)',
	},
});

const LeftToRightKeyframes = keyframes({
	'0%': {
		transform: 'translateX(-3px) rotate(12deg)',
	},
	'100%': {
		transform: 'translateX(-54px) rotate(-12deg)',
	},
});

const FadeOutKeyframes = keyframes({
	'0%': {
		opacity: 1,
	},
	'100%': {
		opacity: 0,
	},
});

const FadeInKeyframes = keyframes({
	'0%': {
		opacity: 0,
	},
	'100%': {
		opacity: 1,
	},
});

const styles = cssMap({
	stackContainer: {
		width: '119px',
		height: '68px',
	},
	centeredContainer: {
		display: 'flex',
		justifyContent: 'center',
	},
	stackLayer: {
		position: 'absolute',
	},
	iconTile: {
		height: '57px',
		width: '57px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		backgroundColor: token('elevation.surface'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderWidth: token('border.width'),
		borderRadius: token('radius.medium'),
	},
	primaryIconTile: {
		transform: 'rotate(-12deg) translateX(3px)',
	},
	secondaryIconTile: {
		transform: 'rotate(12deg) translateX(-3px)',
	},
	transparentIconTile: {
		opacity: 0,
	},
	scaledIcon: {
		// Necessary as the ADS Icon component only support up to 16px
		transform: 'scale(1.75)',
	},
	rightToLeftAnimation: {
		animation: `${RightToLeftKeyframes} 0.3s ease-out both`,
	},
	leftToRightAnimation: {
		animation: `${LeftToRightKeyframes} 0.3s ease-out both`,
	},
	fadeOutAnimation: {
		animation: `${FadeOutKeyframes} 0.3s ease-out both`,
	},
	fadeInAnimation: {
		animation: `${FadeInKeyframes} 0.3s ease-out both`,
	},
});

export const AudioIconStack = ({ featureList }: AudioIconStackProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const initialRender = useRef<boolean>(true);

	const { activeFeature, activeContent, pendingContent } = useAudioPlayerStore();

	const emojiId = pendingContent?.emojiId ?? activeContent.emojiId;
	const contentType = pendingContent?.contentType ?? activeContent.contentType;
	const subType = pendingContent?.subType ?? activeContent.subType;

	const activeFeatureInfo = getFeatureInfo(activeFeature, featureList);

	useEffect(() => {
		initialRender.current = false;
	}, []);

	// Prevent animation on initial render
	const shouldShowAnimation = !initialRender.current;

	const primaryFeature = featureList[0];
	const secondaryFeature = featureList[1];
	const isPrimaryActive = activeFeatureInfo?.name === primaryFeature.name;

	const PrimaryIcon = primaryFeature.icon;
	const SecondaryIcon = secondaryFeature.icon;

	return (
		<Box xcss={styles.centeredContainer}>
			<Box xcss={styles.stackContainer}>
				<Inline alignInline="center" xcss={styles.stackLayer}>
					<Box
						xcss={cx(
							styles.iconTile,
							styles.primaryIconTile,
							isPrimaryActive
								? shouldShowAnimation && styles.fadeInAnimation
								: shouldShowAnimation && styles.fadeOutAnimation,
						)}
						testId="primary-audio-icon-tile"
					>
						<Box xcss={styles.scaledIcon}>
							<PrimaryIcon label={formatMessage(primaryFeature.translationString)} />
						</Box>
					</Box>
					<Box
						xcss={cx(
							styles.iconTile,
							styles.secondaryIconTile,
							isPrimaryActive
								? shouldShowAnimation && styles.fadeOutAnimation
								: shouldShowAnimation && styles.fadeInAnimation,
						)}
						testId="secondary-audio-icon-tile"
					>
						<Box xcss={styles.scaledIcon}>
							<SecondaryIcon label={formatMessage(secondaryFeature.translationString)} />
						</Box>
					</Box>
				</Inline>

				<Inline alignInline="center" xcss={styles.stackLayer}>
					{/* This box is purposely left blank to match the spacing within the other stack layer */}
					<Box xcss={cx(styles.iconTile, styles.primaryIconTile, styles.transparentIconTile)} />

					<Box
						xcss={cx(
							styles.iconTile,
							styles.secondaryIconTile,
							isPrimaryActive
								? shouldShowAnimation && styles.rightToLeftAnimation
								: shouldShowAnimation && styles.leftToRightAnimation,
						)}
						testId="sliding-audio-icon-tile"
					>
						<Box xcss={styles.scaledIcon}>
							<PlaylistItemIcon
								emojiId={emojiId ?? undefined}
								contentType={contentType}
								isLive={subType === 'live'}
							/>
						</Box>
					</Box>
				</Inline>
			</Box>
		</Box>
	);
};
